/** Display formatting. Times are shown in the browser's zone; the API speaks UTC (RFC 3339). */
import type { Money, Quantity } from '@/api';

const d = (v: string | Date) => (typeof v === 'string' ? new Date(v) : v);

export const fmtTime = (v: string | Date | null | undefined) =>
  v ? d(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : '—';

export const fmtDate = (v: string | Date | null | undefined) => {
  if (!v) return '—';
  // Plain dates (YYYY-MM-DD) are calendar dates, not instants: never shift by zone.
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, dd] = v.split('-').map(Number);
    return new Date(y, m - 1, dd).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
  }
  return d(v).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
};

export const fmtShortDate = (v: string | Date | null | undefined) => {
  if (!v) return '—';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, dd] = v.split('-').map(Number);
    return new Date(y, m - 1, dd).toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
  return d(v).toLocaleDateString([], { month: 'short', day: 'numeric' });
};

export const fmtDateTime = (v: string | Date | null | undefined) => (v ? `${fmtShortDate(v)} ${fmtTime(v)}` : '—');

export function isToday(v: string | Date) {
  return d(v).toDateString() === new Date().toDateString();
}

/** "today 09:12", "Sep 24 09:12" */
export const fmtWhen = (v: string | Date | null | undefined) => (!v ? '—' : isToday(v) ? `today ${fmtTime(v)}` : fmtDateTime(v));

/** "in 12 min", "21 min ago", "3 h ago", "2 d ago" */
export function relative(v: string | Date | null | undefined, now = Date.now()): string {
  if (!v) return '—';
  const diff = d(v).getTime() - now;
  const abs = Math.abs(diff);
  const m = Math.round(abs / 60000);
  const unit = m < 60 ? `${m} min` : m < 60 * 36 ? `${Math.round(m / 60)} h` : `${Math.round(m / 1440)} d`;
  if (m === 0) return 'now';
  return diff > 0 ? `in ${unit}` : `${unit} ago`;
}

export function dueInfo(v: string | null | undefined): { label: string; overdue: boolean; soon: boolean } {
  if (!v) return { label: 'No due time', overdue: false, soon: false };
  const ms = d(v).getTime() - Date.now();
  return { label: isToday(v) ? fmtTime(v) : fmtDateTime(v), overdue: ms < 0, soon: ms >= 0 && ms < 2 * 3600_000 };
}

export const todayLocal = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

export const addDays = (iso: string, n: number) => {
  const [y, m, dd] = iso.split('-').map(Number);
  const x = new Date(y, m - 1, dd + n);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

export const fmtLongDate = (iso: string) => {
  const [y, m, dd] = iso.split('-').map(Number);
  return new Date(y, m - 1, dd).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
};

export const fmtMoney = (m: Money | null | undefined) =>
  m ? new Intl.NumberFormat(undefined, { style: 'currency', currency: m.currency }).format(Number(m.amount)) : '—';

export const fmtQty = (q: Quantity | null | undefined) => {
  if (!q) return '—';
  const u = UNIT_LABEL[q.unit] ?? q.unit;
  return `${q.comparator ?? ''}${q.value} ${u}`.trim();
};

/** UCUM → human label (display only; the wire keeps UCUM). */
export const UNIT_LABEL: Record<string, string> = {
  'mm[Hg]': 'mmHg', '/min': '/min', Cel: '°C', '[degF]': '°F', kg: 'kg', '[lb_av]': 'lb', '%': '%', '{score}': '', 'mL/min/{1.73_m2}': 'mL/min/1.73m²', '{tbl}': 'tablets', ug: 'mcg',
};

export const titleCase = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
export const sentence = (s: string) => {
  const t = s.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
};
